import React, { useState, useEffect } from "react";
import styles from "../css/index.js";
import { searchInventory, checkMedicationInItemTable } from "../../../../api/dispensary/script/LabelApi.js";

const MedicationField = ({ 
  form, 
  medications, 
  selectedRxNumber, 
  selectedMedication, 
  setSelectedMedication, 
  setIcd10Codes, 
  setIsMedicationInItemTable,
  onFieldChange,
  prescriptionId 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Enable searchable mode when no RX number is selected OR when coming directly from customer dispensary page
  const enableSearchableMode = !selectedRxNumber;

  useEffect(() => {
    if (enableSearchableMode && searchTerm) {
      searchMedications();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, enableSearchableMode]);

  const searchMedications = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Search through entire inventory using existing API
      const response = await searchInventory(searchTerm);
      if (response.success && response.data) {
        setSearchResults(response.data);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching medications:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleMedicationSelect = async (medication) => {
    setSearchTerm(medication.name);
    setShowDropdown(false);
    setSelectedMedication(medication);
    
    // Check if medication is in item table
    try {
      const result = await checkMedicationInItemTable(medication.name);
      setIsMedicationInItemTable(result.isInItemTable);
      
      // Add SKU to form data if found
      if (result.sku) {
        onFieldChange(form.id, "sku", result.sku);
      }
      
      console.log('MedicationField item table check:', {
        medication: medication.name,
        isInItemTable: result.isInItemTable,
        sku: result.sku
      });
    } catch (error) {
      console.error('Error checking medication in item table:', error);
      setIsMedicationInItemTable(false);
    }
    
    // Auto-fill quantity field if medication information is available
    if (medication && medication.quantity) {
      onFieldChange(form.id, "quantity", medication.quantity.toString());
    }
    
    // Auto-fill pack size field if medication information is available
    if (medication && medication.packsize) {
      onFieldChange(form.id, "packSize", medication.packsize.toString());
    }
    
    // Set ICD-10 codes if available (only if not in item table)
    if (medication && medication.icd10Codes) {
      setIcd10Codes(medication.icd10Codes);
    } else {
      setIcd10Codes([]);
    }
    
    // Set barcode from inventory table
    if (medication) {
      // Try different possible barcode field names from inventory
      const inventoryBarcode = medication.barcode || medication.sku || medication.inventoryBarcode || medication.itemBarcode;
      console.log('Setting barcode field from inventory:', inventoryBarcode);
      console.log('Medication object:', medication);
      if (inventoryBarcode) {
        onFieldChange(form.id, "barcode", inventoryBarcode);
      } else {
        console.log('No barcode found in inventory medication:', medication);
      }
    }
    
    onFieldChange(form.id, "medication", medication.name);
  };

  if (enableSearchableMode) {
    return (
      <div className={styles.inputGroup}>
        <label className={styles.label}>Medication</label>
        <div className={styles.medicationSearchWrapper}>
          <input
            type="text"
            className={styles.input}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder={selectedRxNumber ? "Search medication..." : "Search all medications in inventory..."}
          />
          {showDropdown && (searchResults.length > 0 || isSearching) && (
            <div className={styles.medicationDropdown}>
              {isSearching ? (
                <div className={styles.medicationSearchStatus}>Searching...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((med) => (
                  <div
                    key={med.sku}
                    onClick={() => handleMedicationSelect(med)}
                    className={styles.medicationDropdownItem}
                    onMouseEnter={(e) => e.target.classList.add('hover')}
                    onMouseLeave={(e) => e.target.classList.remove('hover')}
                  >
                    <div className={styles.medicationName}>{med.name}</div>
                    <div className={styles.medicationDetails}>
                      SKU: {med.sku} | Pack: {med.packsize} | Qty: {med.quantity}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.medicationSearchStatus}>No medications found</div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original dropdown mode for when RX number is selected
  return (
    <div className={styles.inputGroup}>
      <label className={styles.label}>Medication</label>
      <select
        className={styles.select}
        value={form.data.medication}
        onChange={async (e) => {
          const selectedMed = medications.find(med => med.name === e.target.value);
          setSelectedMedication(selectedMed);

          // Check if medication is in item table
          if (e.target.value) {
            try {
              const result = await checkMedicationInItemTable(e.target.value);
              setIsMedicationInItemTable(result.isInItemTable);
              
              // Add SKU to form data if found
              if (result.sku) {
                onFieldChange(form.id, "sku", result.sku);
              }
              
              console.log('MedicationField dropdown item table check:', {
                medication: e.target.value,
                isInItemTable: result.isInItemTable,
                sku: result.sku
              });
            } catch (error) {
              console.error('Error checking medication in item table:', error);
              setIsMedicationInItemTable(false);
            }
          } else {
            setIsMedicationInItemTable(false);
          }

          // Auto-fill quantity field if medication information is available
          if (selectedMed && selectedMed.quantity) {
            onFieldChange(form.id, "quantity", selectedMed.quantity.toString());
          }

          // Auto-fill pack size field if medication information is available
          if (selectedMed && selectedMed.packSize) {
            onFieldChange(form.id, "packSize", selectedMed.packSize.toString());
          }

          // Set ICD-10 codes if available (only if not in item table)
          if (selectedMed && selectedMed.icd10Codes) {
            setIcd10Codes(selectedMed.icd10Codes);
          } else {
            setIcd10Codes([]);
          }

          // Set barcode from inventory table
          if (selectedMed) {
            // Try different possible barcode field names from inventory
            const inventoryBarcode = selectedMed.barcode || selectedMed.sku || selectedMed.inventoryBarcode || selectedMed.itemBarcode;
            console.log('Setting barcode field from inventory (dropdown):', inventoryBarcode);
            console.log('Selected medication object:', selectedMed);
            if (inventoryBarcode) {
              onFieldChange(form.id, "barcode", inventoryBarcode);
            } else {
              console.log('No barcode found in selected medication (dropdown):', selectedMed);
            }
          }

          onFieldChange(form.id, "medication", e.target.value);
        }}
      >
        <option value="">{selectedRxNumber ? "Select Medication" : "Select RX Number or Search Below"}</option>
        {medications.map((med) => (
          <option key={med.id} value={med.name}>
            {med.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default MedicationField;